/**
 * Client-side Trip model-specs with custom methods and computed properties
 */

import type { DataOf, InputOf } from '@elara/firebolt/shared';

import type { DestinationsContainer } from '#containers/DestinationsContainer.js';
import type { FirestoreContainer } from '#containers/FirestoreContainer.js';
import type { ClientInstanceOf } from '#firebase/types.js';
import type { AppFlowsContainer } from '#flows.js';
import type { Destination } from '#models/DestinationModel.js';

import { TripModelSpec } from '@elara/domain/models/TripModelSpec';

import { formatDistanceToNow } from 'date-fns';

import { createClientModel } from '#firebase/createClientModel.js';
import { formatDateRange, formatShortDateRange } from '#lib/date-utils.js';
import { getErrorReporter } from '#lib/error-reporting.js';
import { getTripActivityDate } from '#models/trip-activity.js';

/**
 * Trip model for client-side usage
 * Combines the TripSpec with custom methods and attached flows
 */
export function createClientTripModel(
  flows: AppFlowsContainer,
  firestore: FirestoreContainer,
  destinations: DestinationsContainer,
) {
  // Create the destination model and firestore utilities outside the extensions
  const { ClientDestination } = destinations;

  const model = createClientModel(
    firestore,
    TripModelSpec,
    () => ({
      /** Check if a user is a participant in the trip */
      isUserParticipant(userId: string): boolean {
        return this.participants.some((p) => p.id === userId);
      },
      /** Check if a user is the owner of the trip */
      isOwner(userId: string): boolean {
        return this.participants.some((p) => p.id === userId && p.role === 'owner');
      },
      /** Get the display name for the trip */
      get displayName(): string {
        if (this.title && this.title !== 'New Trip') return this.title;
        return 'New Trip';
      },
      /** Get the number of participants in the trip */
      get participantCount(): number {
        return this.participants.length;
      },
      /**
       * A computed signal that returns `true` if the trip is archived.
       *
       * Derived from the `status` enum — the single source of truth for
       * archived/active state — rather than the `archivedAt` timestamp. This
       * keeps the client consistent with the server (`ServerTripModel.isActive`)
       * and with the `TripsState` listener, which filters on `status != 'archived'`.
       */
      get isArchived(): boolean {
        return this.status === 'archived';
      },
      /** Load destinations by their IDs (reactive query with error handling) */
      get destinations(): Destination[] {
        // If no destination IDs, return empty array
        if (!this.destinationIds || this.destinationIds.length === 0) {
          return [];
        }

        try {
          // Use individual document reads instead of collection query with 'in' operator
          // This is more reliable with Firestore security rules
          const destinations: Destination[] = [];
          for (const destinationId of this.destinationIds) {
            const doc = ClientDestination.get({ id: destinationId });
            if (doc.isReady && doc.data) {
              destinations.push(doc.data);
            }
          }
          return destinations;
        } catch (error) {
          getErrorReporter().captureException(error, {
            tags: { module: 'TripModel', action: 'createDestinationsQuery' },
            extra: { destinationIds: this.destinationIds, tripId: this.id },
          });
          return [];
        }
      },

      get formattedDateRange(): string | undefined {
        if (!this.startDate || !this.endDate) {
          return undefined;
        }
        return formatDateRange(this.startDate, this.endDate);
      },

      get formattedShortDateRange(): string | undefined {
        if (!this.startDate || !this.endDate) {
          return undefined;
        }
        return formatShortDateRange(this.startDate, this.endDate);
      },

      /** Trip-card subtitle: the date range, or "Last updated <relative>" when the trip has no dates yet. */
      get cardSubtitle(): string | undefined {
        const range = this.formattedDateRange;
        if (range) return range;
        const lastActivity = getTripActivityDate(this);
        return lastActivity ? `Last updated ${formatDistanceToNow(lastActivity, { addSuffix: true })}` : undefined;
      },

      get formattedDestinations(): string | undefined {
        if (!this.destinations || this.destinations.length === 0) {
          return undefined;
        }

        let fmtDestinations = this.destinations[0]?.displayName;

        if (this.destinations.length > 1) {
          fmtDestinations += ` + ${this.destinations.length - 1}`;
        }

        return fmtDestinations;
      },

      /** Get the cover image URL for the trip - finds the first destination with a valid image */
      get coverImage(): string | undefined {
        if (!this.destinations || this.destinations.length === 0) return undefined;
        for (const destination of this.destinations) {
          if (destination?.images && destination.images.length > 0) {
            for (const image of destination.images) {
              if (image?.url) {
                return image.url;
              }
            }
          }
        }
        return undefined;
      },
    }),
    {
      /** Attached flow for creating a new trip */
      create: flows.createTrip,
      /** Attached flow for updating trip details */
      update: flows.updateTrip,
      /** Attached flow for deleting (archiving) a trip */
      delete: flows.deleteTrip,
      /** Attached flow for restoring an archived trip */
      restore: flows.restoreTrip,
      /** Attached flow for turning on the public read-only itinerary link */
      enableShareLink: flows.enableTripShareLink,
      /** Attached flow for revoking the public itinerary link */
      disableShareLink: flows.disableTripShareLink,
    },
  );
  return model;
}

export type TripModel = ReturnType<typeof createClientTripModel>;
export interface Trip extends ClientInstanceOf<TripModel> {}
export interface TripData extends DataOf<TripModelSpec> {}
export interface TripInput extends InputOf<TripModelSpec> {}
